import { isEmpty } from 'lodash-es';

import { ConnectorNode, EvseController } from 'types/evse-controller';
import { EvseControllerConnector } from 'types/evse-controller';

export const EVSE_CONTROLLER_LIMIT = 10;

export enum PowerType {
  AC = 'AC',
  DC = 'DC',
  ACDC = 'ACDC',
}

export type ConnectorsSelection = {
  [key: ConnectorSelectionKey]: ConnectorSelectionValue;
};

/**
 * ConnectorSelectionValue requires at least:
 * - evseController.opChargingStationId
 * - ocppConnectorId
 * - ocppEvseId
 *
 * That's due to operator platform only storing these fields, all the other fields are metadata
 */
export type ConnectorSelectionValue = {
  opChargingStationId: string;
  ocppConnectorId?: number;
  ocppEvseId?: number;
};

export type ConnectorSelectionValueWithMetadata = ConnectorSelectionValue & {
  evseController: Partial<EvseController>;
  powerType?: string;
  evseId?: string;
  standard?: string;
};

type ConnectorSelectionKey = string;

export const getConnectorSelectionKey = (
  connector: ConnectorSelectionValue
): ConnectorSelectionKey => {
  return `${connector.opChargingStationId}-${connector.ocppEvseId}-${connector.ocppConnectorId}`;
};

/**
 * This function is used to convert a list of evse controllers to a list of connector selection values.
 * ConnectorSelectionValues is a type used to render the list of connectors on the form
 */
export const evseControllersToConnectorSelectionValues = (
  evseControllers: EvseController[],
  filters: { powerType?: string } = {}
): ConnectorSelectionValueWithMetadata[] => {
  return evseControllers?.flatMap((evseController) => {
    if (isEmpty(evseController.connectors)) {
      return [
        {
          evseController,
          opChargingStationId: evseController.id!,
        },
      ];
    }

    return evseController
      .connectors!.map((connector) => {
        const connectorSelectionValue = evseControllerToConnectorSelectionValue(
          evseController,
          connector
        );
        if (!connectorSelectionValue) {
          return {
            evseController,
            opChargingStationId: evseController.id!,
          };
        }

        const powerType = convertPowerTypeStringToPowerType(
          connector.powerType ?? ''
        );
        if (filters.powerType && powerType !== filters.powerType) {
          return null;
        }

        return {
          ...connectorSelectionValue,
          ...connector,
          evseController,
        };
      })
      .filter((connector) => !!connector);
  });
};

export const isConnectorSelectionValueWithMetadataDisabled = (
  connector: ConnectorSelectionValueWithMetadata
): boolean => {
  return !connector.ocppEvseId || !connector.ocppConnectorId;
};

const evseControllerToConnectorSelectionValue = (
  evseController: EvseController,
  connector: EvseControllerConnector
): ConnectorSelectionValue | null => {
  const connectorStatuses: ConnectorNode[] = Array.isArray(
    evseController.connectorStatus
  )
    ? evseController.connectorStatus
    : Object.values(evseController.connectorStatus ?? {});

  const connectorStatus = connectorStatuses.find(
    (c) => c.connectorId === connector.connectorId
  );

  if (
    !evseController.opChargingStationId ||
    !connectorStatus ||
    !connectorStatus.ocppConnectorId ||
    !connectorStatus.ocppEvseId
  ) {
    return null;
  }

  return {
    opChargingStationId: evseController.opChargingStationId,
    ocppConnectorId: connectorStatus.ocppConnectorId,
    ocppEvseId: connectorStatus.ocppEvseId,
  };
};

export type SearchFilter = {
  id: number;
  category: FilterType;
  value: string;
};

export enum FilterType {
  Location = 'location',
  Account = 'account',
  PowerType = 'powerType',
}

export function convertPowerTypeStringToPowerType(
  powerType: string
): PowerType {
  switch (powerType.toUpperCase()) {
    case 'AC_1_PHASE':
      return PowerType.AC;
    case 'AC_3_PHASE':
      return PowerType.AC;
    case 'DC':
      return PowerType.DC;
    case 'ACDC':
      return PowerType.ACDC;
    default:
      return PowerType.AC;
  }
}

export function powerTypeToFriendlyName(powerType: string) {
  switch (powerType.toUpperCase()) {
    case 'AC_1_PHASE':
      return '1-Phase AC';
    case 'AC_3_PHASE':
      return '3-Phase AC';
    case 'DC':
      return 'DC';
    default:
      return powerType;
  }
}

export function standardToFriendlyName(standard: string) {
  switch (standard.toUpperCase()) {
    case 'IEC_62196_T1':
      return 'Type 1';
    case 'IEC_62196_T1_COMBO':
      return 'Type 1 Combo';
    case 'IEC_62196_T2':
      return 'Type 2';
    case 'IEC_62196_T2_COMBO':
      return 'Type 2 Combo';
    case 'IEC_62196_T3A':
      return 'Type 3';
    case 'IEC_62196_T3C':
      return 'Type 3';
    case 'CHADEMO':
      return 'CHAdeMO';
    case 'DOMESTIC_F':
      return 'Domestic F';
    case 'DOMESTIC_E':
      return 'Domestic E';
    case 'DOMESTIC_B':
      return 'Domestic B';
    case 'CATARC':
      return 'Catarc';
    default:
      return standard;
  }
}
